import { memo, useCallback, useMemo, useState } from "react";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { Empty, Input, List, Spin, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { EmployeeListDto } from "../../../../models/dtos/EmployeeList.dto";
import { CommonAvatar } from "../avatar/CommonAvatar";
import { employeeService } from "../../../hrm/services/EmployeeService";

interface PickedEmployee {
  userId: string;
  fullName: string;
  avatarId?: string;
}

interface GroupMemberPickerProps {
  value: string[];
  onChange: (userIds: string[]) => void;
  excludeUserIds?: string[];
  placeholder?: string;
}

const EMPTY_USER_IDS: string[] = [];

export const GroupMemberPicker = memo(({ value, onChange, excludeUserIds = EMPTY_USER_IDS, placeholder }: GroupMemberPickerProps) => {
  const [keyword, setKeyword] = useState("");
  const [pickedMap, setPickedMap] = useState<Map<string, PickedEmployee>>(new Map());

  const trimmed = keyword.trim();
  const { data, isFetching } = useQuery({
    queryKey: ["chat", "group-member-search", trimmed],
    queryFn: () => employeeService.search(trimmed, 0, 10),
    enabled: trimmed.length >= 2,
    staleTime: 60_000,
  });

  const excludeSet = useMemo(() => new Set(excludeUserIds), [excludeUserIds]);
  const valueSet = useMemo(() => new Set(value), [value]);

  const employees = useMemo(() => (data?.content ?? []).filter((e) => e.userId && !excludeSet.has(e.userId)), [data?.content, excludeSet]);

  const toggle = useCallback((e: EmployeeListDto) => {
    if (valueSet.has(e.userId)) {
      onChange(value.filter((id) => id !== e.userId));
    } else {
      setPickedMap((prev) => new Map(prev).set(e.userId, { userId: e.userId, fullName: e.fullName, avatarId: e.avatarId }));
      onChange([...value, e.userId]);
    }
  }, [onChange, value, valueSet]);

  const remove = useCallback((userId: string) => onChange(value.filter((id) => id !== userId)), [onChange, value]);

  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((userId) => {
            const picked = pickedMap.get(userId);
            return (
              <span key={userId} className="flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-xs">
                <CommonAvatar size={18} src={picked?.avatarId} name={picked?.fullName ?? userId} className="bg-blue-500 text-white" />
                <span className="max-w-[120px] truncate text-neutral-700">{picked?.fullName ?? userId}</span>
                <CloseOutlined className="text-neutral-400 hover:text-neutral-700 cursor-pointer" style={{ fontSize: 10 }} onClick={() => remove(userId)} />
              </span>
            );
          })}
        </div>
      )}

      <Input
        allowClear
        size="small"
        prefix={<SearchOutlined className="text-neutral-400" />}
        placeholder={placeholder ?? "Tìm nhân viên để thêm..."}
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
      />

      {trimmed.length >= 2 && (
        <div className="max-h-[220px] overflow-y-auto border rounded-lg bg-neutral-50">
          {isFetching ? (
            <div className="flex justify-center py-6">
              <Spin size="small" />
            </div>
          ) : employees.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không tìm thấy" className="py-4" />
          ) : (
            <List
              split={false}
              dataSource={employees}
              renderItem={(e) => {
                const selected = valueSet.has(e.userId);
                return (
                  <List.Item className="!px-2 !py-1">
                    <div
                      onClick={() => toggle(e)}
                      className={`w-full flex items-center gap-2 p-1.5 rounded-lg cursor-pointer transition-all ${
                        selected ? "bg-blue-100" : "bg-white hover:bg-neutral-100"
                      }`}
                    >
                      <CommonAvatar size={32} src={e.avatarId} name={e.fullName} className="bg-blue-500 text-white" />
                      <div className="flex-1 min-w-0">
                        <Typography.Text ellipsis className="font-medium text-sm text-neutral-800 block">
                          {e.fullName}
                        </Typography.Text>
                        <div className="text-xs text-neutral-500 truncate">{[e.employeeCode, e.jobTitle].filter(Boolean).join(" - ")}</div>
                      </div>
                      {selected && <span className="text-xs text-blue-600 font-medium shrink-0">Đã chọn</span>}
                    </div>
                  </List.Item>
                );
              }}
            />
          )}
        </div>
      )}
    </div>
  );
});
